<template>
  <div class="container">
    <transition-group name="slide" mode="out-in">
      <div v-show="!showDeletionPage" key="section-0">
        <h5>Editar {{ accountName }}</h5>
        <form @submit.prevent="handleClick">
          <label for="account-type"
            >Tipo de Conta
            <ul id="account-type" class="account-type-list">
              <li
                v-for="{ id, icon_name, name } in accountTypeList"
                :key="id"
                class="account-type-list-item"
                :id="id"
                @click="selectAccountType">
                <AppIcon :iconName="icon_name" :class="icon_name" />
                {{ name }}
              </li>
          </ul>
          </label>
          <label for="account-name">
            Nome
            <input
              v-model="accountName"
              type="text"
              inputmode="text"
              name="account-name"
              id="account-name"
              required
            />
          </label>
          <div class="label mb-r">
            <label for="account-initial-balance" class="account-initial-balance">
              Montante atual
              <input
                disabled
                type="number"
                inputmode="decimal"
                autocomplete="transaction-amount"
                name="account-initial-balance"
                v-model="currentBalance"
                required
              />
            </label>
            <label for="account-currency" class="account-currency">
            Moeda
            <select name="account-currency" id="account-currency" v-model="currencyId"
              required @change="recalculateBalance">
              <option v-for="{ id, short_name, inUse } in currencyListWithStatuses"
                disabled :key="id" :value="id" :selected="inUse" >{{short_name}}</option>
            </select>
          </label>
          </div>
          <span v-if="wasTheUpdateSuccessful"
            @click="wasTheUpdateSuccessful = !wasTheUpdateSuccessful" class="success-message">
            Dados salvos com sucesso! &#9932;</span>
          <AppButton id="saveChanges" class="save-button" text="Salvar alterações" />
          <p @click="goToRoute" id="/contas" class="cancel-button" role="button">
            Voltar ao início</p>
          <AppButton id="deleteAccount" class="delete-button" text="Apagar Conta" />
        </form>
      </div>
      <div v-show="showDeletionPage" class="account-editing-section-1" key="section-1">
        <h5>Confirmar acção</h5>
        <p>
          Esta acção é irreversível. O histórico desta conta será eliminado de forma permanente.
        </p>
        <p>Deseja mesmo apagar a conta?</p>
        <span>
          <AppButton @click.native="cancelOrConfirmDeletion" text="Nao" id="no" />
          <AppButton @click.native="cancelOrConfirmDeletion" text="Sim" id="yes" />
        </span>
      </div>
    </transition-group>
    <TheBottomNav :linksList="linksList" @click.native="changeRoute"/>
  </div>
</template>

<script>
import axios from "axios";
import convertCurrencyFrom from "@/misc/currency";
import AppButton from "@/components/AppButton.vue";
import AppIcon from "@/components/AppIcon.vue";
import TheBottomNav from "@/components/TheBottomNavbar.vue";
import { requestBuilder } from "@/misc/util";

export default {
  name: "CreateAccount",
  components: {
    AppButton,
    AppIcon,
    TheBottomNav
  },
  created() {
    this.getCurrencies();
    this.getAccountTypes();
    this.setAccountData();
  },
  beforeMount() {
    if (this.accountData) this.setAccountData();
  },
  mounted() {
    setTimeout(() => this.selectAccountType(), 1000);
  },
  data() {
    return {
      accountData: null,
      accountID: "",
      accountTypeId: "",
      currentBalance: null,
      backupBalance: null,
      currencyId: "",
      accountCurrency: null,
      backupCurrency: null,
      accountName: "",
      accountTypeList: [],
      currencyList: [],
      linksList: [],
      showDeletionPage: false,
      wasTheUpdateSuccessful: false
    };
  },
  computed: {
    currencyListWithStatuses() {
      return this.currencyList.map(c => {
        if (c.short_name === this.accountCurrency) {
          return {
            short_name: c.short_name,
            id: c.id,
            inUse: true
          };
        }
        return { short_name: c.short_name, id: c.id, inUse: false };
      });
    },
    accountTypeListWithStatuses() {
      return this.accountTypeList.map(a => {
        if (a.id === this.accountTypeId) {
          return {
            id: a.id,
            name: a.name,
            icon_name: a.icon_name,
            inUse: true
          };
        }
        return {
          id: a.id,
          name: a.name,
          icon_name: a.icon_name,
          inUse: false
        };
      });
    }
  },
  methods: {
    changeRoute(e) {
      if (e.srcElement.id) {
        const { srcElement: { id } } = e;
        if (this.$route.name !== id) {
          this.$router.push({ name: id,
            params: { accountID: this.accountID,
              accountData: this.$route.params.accountData } });
        }
      }
    },
    goToRoute(e) {
      const nextRoute = e.srcElement.id;
      if (nextRoute) {
        const currentRoute = this.$route.path;

        if (currentRoute !== nextRoute) this.$router.push(nextRoute);
      }
    },
    setAccountData() {
      if (this.$route.params.accountData) {
        this.accountData = this.$route.params.accountData;
        this.accountTypeId = this.accountData.accountType.id;
        this.accountID = this.$route.params.accountData.id;
        this.accountOwnerID = this.$route.params.accountData.user_id;
        this.accountName = this.$route.params.accountData.account_name;
        this.currentBalance = this.$route.params.accountData.current_balance.toFixed(2);
        this.backupBalance = this.currentBalance;
        this.accountCurrency = this.$route.params.accountData.currency.short_name;
        this.currencyId = this.$route.params.accountData.currency.id;
        this.backupCurrency = this.accountCurrency;
        this.linksList = [
          {
            routeName: "accountTransactions",
            name: "Transacções",
            icon: "transactions",
            path: `/contas/${this.accountID}/transaccoes`
          },
          {
            routeName: "accountReport",
            name: "Relatório",
            icon: "report",
            path: `/contas/${this.accountID}/relatorios`
          },
          {
            routeName: "accountSettings",
            name: "Dados da conta",
            icon: "settings",
            path: `/contas/${this.accountID}/definicoes`
          }
        ];
      } else this.$router.push({ name: "accounts" });
    },
    async recalculateBalance() {
      const { short_name: newCurrencyShortName } = this.currencyListWithStatuses
        .filter(c => c.id === this.currencyId)[0];
      try {
        const multiplier = await convertCurrencyFrom(this.backupCurrency, newCurrencyShortName);
        const newBalance = parseFloat((this.currentBalance * multiplier).toFixed(2));
        this.currentBalance = newBalance;
        this.backupCurrency = newCurrencyShortName;
      } catch (error) {
        console.log(error);
      }
    },
    setNewValues(accountData, accountType, currency) {
      this.$route.params.accountData.accountType = accountType;
      this.$route.params.accountData.currency = currency;
      this.$route.params.accountData.account_name = accountData.account_name;
      this.$route.params.accountData.account_type_id = accountType.id;
      this.$route.params.accountData.currency_id = currency.id;
      this.$route.params.accountData.updated_at = accountData.updated_at;
      this.$route.params.accountData.current_balance = accountData.current_balance;
      this.accountTypeId = accountType.id;
      this.currencyId = currency.id;
      this.accountName = accountData.account_name;
    },
    async cancelOrConfirmDeletion(e) {
      const { id } = e.srcElement;
      if (id === "no") this.showDeletionPage = false;
      else {
        const url = `/accounts/${this.accountID}`;
        const method = "DELETE";
        const requestOptions = requestBuilder(url, method);

        try {
          await axios.request(requestOptions);
          this.$router.push({ name: "accounts" });
        } catch (error) {
          console.log(error);
        }
      }
    },
    async handleClick(e) {
      const { id } = e.submitter;
      if (id === "deleteAccount") {
        this.showDeletionPage = true;
      } else {
        const accData = {
          userId: this.accountOwnerID,
          currencyId: this.currencyId,
          accountTypeId: this.accountTypeId,
          currentBalance: this.currentBalance,
          accountName: this.accountName,
        };

        const url = `/accounts/${this.accountID}`;
        const method = "PUT";
        const requestOptions = requestBuilder(url, method, accData);

        try {
          const { data: { accountType,
            accountData,
            currency } } = await axios.request(requestOptions);
          this.setNewValues(accountData, accountType, currency);
          this.wasTheUpdateSuccessful = true;
        } catch (error) {
          console.log(error);
        }
      }
    },
    async getAccountTypes() {
      const url = "/account-types";
      const method = "GET";
      const requestOptions = requestBuilder(url, method);

      try {
        const { data } = await axios.request(requestOptions);
        this.accountTypeList = data.map(at => ({
          id: at.id,
          name: at.name,
          created_at: at.created_at,
          updated_at: at.updated_at,
          icon_name: `${at.icon_name}Small`
        }));
      } catch (error) {
        console.log(error);
      }
    },
    async getCurrencies() {
      const url = "/currencies";
      const method = "GET";
      const requestOptions = requestBuilder(url, method);

      try {
        const { data } = await axios.request(requestOptions);
        this.currencyList = data;
      } catch (error) {
        console.log(error);
      }
    },
    selectAccountType(e) {
      if (e === undefined) {
        const id = this.accountTypeId;
        const element = document.querySelector(`#${CSS.escape(id)}`);
        const elementIcon = document.querySelector(`#${CSS.escape(id)} svg`);
        const selectedElement = document.querySelector(".selected-account-type");
        const selectedElementIcon = document.querySelector("svg.selected-account-type");

        if (selectedElement !== null) {
          selectedElement.classList.toggle("selected-account-type");
          selectedElementIcon.classList.toggle("selected-account-type");

          element.classList.toggle("selected-account-type");
          elementIcon.classList.toggle("selected-account-type");
        } else {
          elementIcon.classList.toggle("selected-account-type");
          element.classList.toggle("selected-account-type");
        }
      } else {
        const { id } = e.srcElement;
        this.accountTypeId = id;

        const element = document.querySelector(`#${CSS.escape(id)}`);
        const elementIcon = document.querySelector(`#${CSS.escape(id)} svg`);
        const selectedElement = document.querySelector(".selected-account-type");
        const selectedElementIcon = document.querySelector("svg.selected-account-type");

        if (selectedElement !== null) {
          selectedElement.classList.toggle("selected-account-type");
          selectedElementIcon.classList.toggle("selected-account-type");

          element.classList.toggle("selected-account-type");
          elementIcon.classList.toggle("selected-account-type");
        } else {
          elementIcon.classList.toggle("selected-account-type");
          element.classList.toggle("selected-account-type");
        }
      }
    }
  }
};
</script>

<style scoped>
h5 {
  margin-bottom: 0.8rem;
}

label {
  color: var(--color-tertiary);
}

.label,
label {
  margin-bottom: 1.6rem;
}

button {
  margin: 0.4rem auto 1.6rem auto;
  background-color: var(--color-primary);
  color: var(--color-quaternary);
}

.save-button {
  margin: 0.4rem auto 2rem auto;
}

.delete-button {
  background-color: var(--color-senary);
  margin: 0 auto 1.6rem auto;
}

.cancel-button {
  color: var(--color-secondary);
  text-align: center;
  margin: 0 auto 2rem auto;
  cursor: pointer;
}

.container {
  display: flex;
  flex-direction: column;
  padding-bottom: 4.8rem;
}

.account-editing-section form {
  width: 100%;
  margin-bottom: 1.6rem;
}

.account-editing-section-1 button {
  margin: 0;
}

.account-editing-section-1 p:nth-child(2),
.account-editing-section-1 p:nth-child(3) {
  color: var(--color-tertiary);
}

.account-editing-section-1 :nth-child(3) {
  text-align: center;
  font-size: 0.75rem;
  margin: 1.6rem 0 1.8rem 0;
}

.account-editing-section-1 span {
  display: flex;
  justify-content: center;
}

.account-editing-section-1 span button:first-child {
  margin-right: 2rem;
}

.account-editing-section-1 span :nth-child(2) {
  background-color: var(--color-senary);
}

.account-type-list {
  display: flex;
  flex: 0 0 100%;
  justify-content: space-between;
  flex-direction: column;
}

.account-type-list-item {
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: var(--color-white);
  color: var(--color-primary);
  font-size: 1rem;
  border-radius: 10px;
  padding: 0.6rem;
  width: 100%;
  margin-bottom: 0.8rem;
}

.account-type-list-item svg {
  margin-right: 0.6rem;
}

.label {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
}

.account-currency {
  margin-bottom: 1.6rem;
  width: 100%;
  grid-column: 3/4;
}

.account-currency select {
  font-weight: bold;
  background-color: #c1c1c1;
  pointer-events: none;
}

.account-initial-balance {
  grid-column: 1/3;
}

.account-initial-balance input {
  font-weight: bold;
  background-color: #c1c1c1;
}

.label label :is(input, select) {
  margin-bottom: 0;
}

.mb-r {
  margin-bottom: 0;
}

.selected-account-type {
  background-color: var(--color-primary);
  color: var(--color-quaternary);
}

.success-message {
  font-size: 0.833rem;
  color: var(--color-white);
  padding: 0.4rem 0.8rem;
  border-radius: 0.8rem;
  background-color: var(--color-secondary);
  width: fit-content;
  text-align: center;
  margin: 0 auto 0.6rem auto!important;
}

svg {
  fill: var(--color-primary);
}

svg.selected-account-type {
  fill: var(--color-quinary);
}
</style>
